import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, MessageCircle, Mail } from "lucide-react";
import { getGeneralFAQs } from "./GeneralFAQsData";
import { useState } from "react";
import { useRegion } from "../../../regions/RegionContext";

const GeneralFAQs = () => {
  const { config } = useRegion();
  const [openIndex, setOpenIndex] = useState(null);
  const generalFAQs = getGeneralFAQs(config.terminology);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
       <div className="bg-white">
    <section className="py-20 px-6 max-w-7xl mx-auto relative bg-white">
      <div className=" mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#1b7840] text-sm font-bold uppercase tracking-wider mb-6">
            Got Questions?
          </p>
          <div className="inline-block">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 leading-tight">
              Frequently{" "}
              <span className="relative inline-block">
                <span className="relative z-10 italic font-serif" style={{ color: "#1b7840" }}>Asked</span>
                <motion.svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="12"
                  viewBox="0 0 200 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  <path
                    d="M2 10C50 2, 150 2, 198 10"
                    stroke="#1b7840"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                  />
                </motion.svg>
              </span>{" "}
              Questions
            </h2>
          </div>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mt-8">
            Find answers to common questions about Terces and how we can help transform your business
          </p>
        </motion.div>

        {/* FAQ List */}
        <div className="space-y-4">
          {generalFAQs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl hover:border-[#1b7840]/30 transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left hover:bg-[#F6F4EF]/50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 flex-1">
                    {faq.question}
                  </span>
                  <div
                    className={`w-9 h-9 rounded-xl bg-gradient-to-br from-[#1b7840]/10 to-[#145c32]/10 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      isOpen ? "rotate-180 bg-gradient-to-br from-[#1b7840]/20 to-[#145c32]/20" : ""
                    }`}
                  >
                    {isOpen ? (
                      <Minus className="w-5 h-5 text-[#1b7840]" />
                    ) : (
                      <Plus className="w-5 h-5 text-[#1b7840]" />
                    )}
                  </div>
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 pt-4 bg-gradient-to-b from-[#F6F4EF]/40 to-[#F6F4EF]/60 border-t border-gray-200">
                        <p className="text-gray-700 leading-relaxed text-base">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center bg-gradient-to-br from-[#F6F4EF] to-white rounded-2xl p-8 border-2 border-[#1b7840]/20 shadow-lg"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            Still Have Questions?
          </h3>
          <p className="text-gray-600 mb-6">
            Our team is here to help. Get in touch for personalized answers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/919281115822?text=Hi, I have a question about Terces services."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#25D366] to-[#1EBE57] hover:from-[#20ba5a] hover:to-[#17a34a] text-white px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-[#25D366]/40 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Us
            </a>
            <a
              href="/Contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1b7840] to-[#145c32] text-white px-6 py-3 rounded-xl font-semibold hover:shadow-xl hover:shadow-[#1b7840]/30 transition-all transform hover:scale-105 justify-center"
            >
              <Mail className="w-5 h-5" />
              Contact Us
            </a>
          </div>
        </motion.div>
      </div>
    </section>
        </div>
  );
};

export default GeneralFAQs;